import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, X, Check, Layers } from "lucide-react";

export default function Tables() {
  const [items, setItems] = useState([]);
  const [name, setName] = useState("");
  const [room, setRoom] = useState("Sala A");
  const [seats, setSeats] = useState(4);
  const [editing, setEditing] = useState(null); // {id, name, room, seats}
  const [renamingRoom, setRenamingRoom] = useState(null); // old name
  const [newRoomName, setNewRoomName] = useState("");

  const load = () => api.get("/tables").then((r) => setItems(Array.isArray(r.data) ? r.data : []));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tables", { name, room, seats });
      toast.success("Tavolo creato"); setName(""); load();
    } catch { toast.error("Errore"); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare il tavolo?")) return;
    await api.delete(`/tables/${id}`);
    load();
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      await api.patch(`/tables/${editing.id}`, {
        name: editing.name, room: editing.room, seats: editing.seats,
        map_x: editing.map_x === "" || editing.map_x == null ? null : Number(editing.map_x),
        map_y: editing.map_y === "" || editing.map_y == null ? null : Number(editing.map_y),
      });
      toast.success("Tavolo aggiornato");
      setEditing(null); load();
    } catch { toast.error("Errore"); }
  };

  const doRenameRoom = async () => {
    const nn = (newRoomName || "").trim();
    if (!nn) return toast.error("Nome sala vuoto");
    try {
      const r = await api.post("/tables/rename-room", { old_name: renamingRoom, new_name: nn });
      toast.success(`${r.data.updated} tavoli aggiornati`);
      setRenamingRoom(null); setNewRoomName(""); load();
    } catch { toast.error("Errore rinomina"); }
  };

  const grouped = items.reduce((acc, t) => { (acc[t.room] = acc[t.room] || []).push(t); return acc; }, {});
  const rooms = Object.keys(grouped);

  return (
    <div className="p-8 lg:p-12 max-w-5xl">
      <div className="text-xs font-mono uppercase tracking-[0.25em] text-velta-mute mb-3">Sala</div>
      <h1 className="font-heading text-4xl font-bold tracking-tight mb-2">Tavoli & Sale</h1>
      <p className="text-velta-mute mb-8">
        Imposta sale e tavoli del locale. Puoi <strong>rinominare</strong> una sala o un tavolo, posizionarli sulla mappa (X/Y %) e in cassa unirli o dividerli.
      </p>

      <form onSubmit={add} className="bg-white border border-velta-border rounded-md p-5 mb-6 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[160px]">
          <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Nome tavolo</label>
          <input data-testid="table-name-input" required value={name} onChange={(e) => setName(e.target.value)} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]" placeholder="A1, T5, Bar-1..." />
        </div>
        <div className="flex-1 min-w-[160px]">
          <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Sala</label>
          <input
            data-testid="table-room-input"
            list="rooms-list"
            value={room}
            onChange={(e) => setRoom(e.target.value)}
            className="h-11 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]"
            placeholder="Sala A, Terrazza, Piano 1..."
          />
          <datalist id="rooms-list">
            {(Array.isArray(rooms) ? rooms : []).map((r) => <option key={r} value={r} />)}
          </datalist>
        </div>
        <div className="w-28">
          <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Coperti</label>
          <input type="number" value={seats} onChange={(e) => setSeats(parseInt(e.target.value) || 0)} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B]" />
        </div>
        <button data-testid="table-add-btn" className="h-11 px-5 bg-[#1E2D5B] hover:bg-[#162244] text-white rounded-md font-medium flex items-center gap-2 btn-tactile">
          <Plus className="w-4 h-4" /> Aggiungi
        </button>
      </form>

      <div className="space-y-6">
        {Object.entries(grouped).map(([r, ts]) => (
          <div key={r} className="bg-white border border-velta-border rounded-md p-5">
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-velta-border">
              {renamingRoom === r ? (
                <div className="flex items-center gap-2 flex-1">
                  <Layers className="w-4 h-4 text-velta-mute" />
                  <input
                    autoFocus
                    value={newRoomName}
                    onChange={(e) => setNewRoomName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") doRenameRoom(); if (e.key === "Escape") setRenamingRoom(null); }}
                    className="h-9 flex-1 max-w-xs px-3 border border-velta-ink rounded-md text-sm font-heading font-semibold outline-none"
                  />
                  <button onClick={doRenameRoom} className="text-velta-success"><Check className="w-4 h-4" /></button>
                  <button onClick={() => setRenamingRoom(null)} className="text-velta-mute"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <button
                  data-testid={`room-rename-${r}`}
                  onClick={() => { setRenamingRoom(r); setNewRoomName(r); }}
                  className="flex items-center gap-2 group"
                  title="Rinomina l'intera sala"
                >
                  <Layers className="w-4 h-4 text-velta-mute" />
                  <span className="font-heading font-semibold text-lg">{r}</span>
                  <Edit3 className="w-3.5 h-3.5 opacity-0 group-hover:opacity-60" />
                </button>
              )}
              <span className="text-xs font-mono uppercase tracking-widest text-velta-mute">{ts.length} tavol{ts.length === 1 ? "o" : "i"}</span>
            </div>

            <div className="relative h-52 bg-[#F4F4F5] border border-dashed border-velta-border rounded-md mb-4" data-testid={`room-map-${r}`}>
              {(Array.isArray(ts) ? ts : []).map((t, i) => (
                <div
                  key={`map-${t.id}`}
                  className="absolute w-10 h-10 -ml-5 -mt-5 rounded-md bg-white border border-velta-ink flex items-center justify-center text-xs font-bold"
                  style={{
                    left: `${t.map_x != null ? t.map_x : ((i % 6) * 14 + 10)}%`,
                    top: `${t.map_y != null ? t.map_y : (Math.floor(i / 6) * 28 + 18)}%`,
                  }}
                  title={t.name}
                >
                  {t.name}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
              {(Array.isArray(ts) ? ts : []).map((t) => (
                <div key={t.id} className="group bg-velta-bg border border-velta-border rounded-md p-4 text-center relative hover:border-velta-ink transition-colors">
                  <div className="font-heading font-bold text-2xl">{t.name}</div>
                  <div className="text-xs text-velta-mute font-mono">{t.seats} cop.</div>
                  <div className="absolute top-1 right-1 flex gap-1">
                    <button
                      onClick={() => setEditing({ id: t.id, name: t.name, room: t.room, seats: t.seats, map_x: t.map_x ?? "", map_y: t.map_y ?? "" })}
                      className="opacity-0 group-hover:opacity-100 text-velta-ink hover:text-velta-ink/70"
                      title="Modifica"
                      data-testid={`table-edit-${t.name}`}
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => del(t.id)}
                      className="opacity-0 group-hover:opacity-100 text-velta-error"
                      title="Elimina"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="p-12 text-center text-velta-mute">Nessun tavolo configurato.</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-md w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading text-xl font-semibold">Modifica tavolo</h3>
              <button onClick={() => setEditing(null)}><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Nome</label>
                <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]" />
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Sala</label>
                <input list="rooms-list" value={editing.room} onChange={(e) => setEditing({ ...editing, room: e.target.value })} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 outline-none focus:border-[#1E2D5B]" />
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Coperti</label>
                <input type="number" value={editing.seats} onChange={(e) => setEditing({ ...editing, seats: parseInt(e.target.value) || 0 })} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 font-mono outline-none focus:border-[#1E2D5B]" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Mappa X %</label>
                  <input type="number" min="0" max="100" value={editing.map_x ?? ""} onChange={(e) => setEditing({ ...editing, map_x: e.target.value })} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 font-mono" placeholder="20" />
                </div>
                <div>
                  <label className="block text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">Mappa Y %</label>
                  <input type="number" min="0" max="100" value={editing.map_y ?? ""} onChange={(e) => setEditing({ ...editing, map_y: e.target.value })} className="h-11 w-full bg-white border border-velta-border rounded-md px-3 font-mono" placeholder="40" />
                </div>
              </div>
            </div>
            <button data-testid="table-edit-save" onClick={saveEdit} className="w-full mt-6 h-12 bg-[#1E2D5B] hover:bg-[#162244] text-white rounded-md font-medium btn-tactile">Salva</button>
          </div>
        </div>
      )}
    </div>
  );
}
